import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import type { AgentGroup } from '@/lib/types';

/**
 * The org's agent groups and the mutations that maintain them.
 *
 * Membership is stored as an association on each agent, so every write here
 * changes two things the UI reads: the group list (member counts) and the
 * agents themselves (their group chips). Unlike archive/pin — which settle
 * through the `agent:updated` broadcast the server emits — a membership write
 * touches no agent row, so there is nothing to broadcast; these hooks
 * invalidate the agent queries directly instead. That is a one-shot refetch
 * on mutation, not polling.
 */

/** Agent-list caches that carry group chips and must refetch after a membership write. */
const AGENT_LIST_KEY_PREFIXES = ['agents', 'pinned-agents', 'agent-search', 'agent'] as const;

function useInvalidateGroupData() {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.agentGroups(orgKey) });
    for (const prefix of AGENT_LIST_KEY_PREFIXES) {
      void queryClient.invalidateQueries({ queryKey: [prefix, orgKey] });
    }
  }, [queryClient, orgKey]);
}

/** Every group in the active org, oldest first, with its member count. */
export function useAgentGroups() {
  const orgKey = useOrgKey();
  const key = useMemo(() => [...queryKeys.agentGroups(orgKey)], [orgKey]);
  return useQuery<AgentGroup[]>({
    queryKey: key,
    queryFn: async () => {
      // The generated client types this response but the transport hands back
      // whatever the server sent, so an empty body (a surface with no groups
      // endpoint stubbed, a 204) reads as "no groups" rather than crashing the
      // query with an undefined result.
      const res = await api.listAgentGroups();
      return res?.groups ?? [];
    },
  });
}

/**
 * A duplicate name comes back as a 409 with the server's message; surface it
 * verbatim rather than a generic failure, since the name is what the user has
 * to change.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && typeof error.body?.error === 'string') return error.body.error;
  return fallback;
}

/** Create a group, optionally filing a selection into it in the same call. */
export function useCreateAgentGroup() {
  const invalidate = useInvalidateGroupData();
  return useMutation({
    mutationFn: async (input: { name: string; agentIds?: string[] }) => {
      const res = await api.createAgentGroup(input);
      return res.group;
    },
    onSuccess: (group) => {
      invalidate();
      toast.success(
        group.memberCount > 0
          ? `Created ${group.name} with ${countLabel(group.memberCount)}`
          : `Created ${group.name}`,
      );
    },
    onError: (error) => toast.error(errorMessage(error, 'Failed to create group')),
  });
}

export function useRenameAgentGroup() {
  const invalidate = useInvalidateGroupData();
  return useMutation({
    mutationFn: async (input: { groupId: string; name: string }) => {
      const res = await api.renameAgentGroup(input.groupId, input.name);
      return res.group;
    },
    onSuccess: (group) => {
      invalidate();
      toast.success(`Renamed group to ${group.name}`);
    },
    onError: (error) => toast.error(errorMessage(error, 'Failed to rename group')),
  });
}

/** Delete a group. The agents that were in it are untouched. */
export function useDeleteAgentGroup() {
  const invalidate = useInvalidateGroupData();
  return useMutation({
    mutationFn: (input: { groupId: string; name: string }) => api.deleteAgentGroup(input.groupId),
    onSuccess: (_result, input) => {
      invalidate();
      toast.success(`Deleted ${input.name}`);
    },
    onError: (error) => toast.error(errorMessage(error, 'Failed to delete group')),
  });
}

/**
 * File agents into a group. Membership is exclusive, so the server drops any
 * group these agents were already in; `moved` only decides which verb the
 * toast uses, since the caller is the one that knows where they came from.
 */
export function useAddAgentsToGroup() {
  const invalidate = useInvalidateGroupData();
  return useMutation({
    mutationFn: async (input: { groupId: string; agentIds: string[]; moved?: boolean }) => {
      const res = await api.addAgentsToGroup(input.groupId, input.agentIds);
      return res.group;
    },
    onSuccess: (group, input) => {
      invalidate();
      const verb = input.moved ? 'Moved' : 'Added';
      toast.success(`${verb} ${countLabel(input.agentIds.length)} to ${group.name}`);
    },
    onError: (error) => toast.error(errorMessage(error, 'Failed to add agents to the group')),
  });
}

export function useRemoveAgentsFromGroup() {
  const invalidate = useInvalidateGroupData();
  return useMutation({
    mutationFn: async (input: { groupId: string; agentIds: string[] }) => {
      const res = await api.removeAgentsFromGroup(input.groupId, input.agentIds);
      return res.group;
    },
    onSuccess: (group, input) => {
      invalidate();
      toast.success(`Removed ${countLabel(input.agentIds.length)} from ${group.name}`);
    },
    onError: (error) => toast.error(errorMessage(error, 'Failed to remove agents from the group')),
  });
}

function countLabel(count: number): string {
  return `${count} agent${count === 1 ? '' : 's'}`;
}
